import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

function PictionaryWordsPanel() {
  const [words, setWords] = useState([]);
  const [newWord, setNewWord] = useState("");
  const [bulkText, setBulkText] = useState("");

  const refresh = () => api.get("/pictionary/admin/words").then((r) => setWords(r.data));
  useEffect(() => { refresh(); }, []);

  const addOne = async () => {
    const w = newWord.trim();
    if (!w) return;
    try {
      await api.post("/pictionary/admin/words", { word: w });
      setNewWord("");
      toast.success("Word added");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const addBulk = async () => {
    const arr = bulkText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (arr.length === 0) return;
    try {
      const r = await api.post("/pictionary/admin/words/bulk", { words: arr });
      toast.success(`Added ${r.data.added} word${r.data.added === 1 ? "" : "s"}`);
      setBulkText("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk add failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/pictionary/admin/words/${id}`);
    toast.success("Removed");
    refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-black uppercase flex items-center gap-2"><Pencil size={18}/> Pictionary Custom Words</h3>
        <span className="text-xs text-neutral-600">{words.length} custom · 36 built-in</span>
      </div>

      <div className="border-2 border-black p-4 grid gap-3 bg-white">
        <h4 className="text-sm font-black uppercase">Add one</h4>
        <div className="flex gap-2">
          <input
            data-testid="picti-word-input"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addOne()}
            placeholder="e.g. ALL-HANDS"
            className="brutal-input flex-1 uppercase"
          />
          <button data-testid="picti-word-add" onClick={addOne} className="brutal-btn">Add</button>
        </div>
        <h4 className="text-sm font-black uppercase mt-2">Bulk paste (one per line or comma-separated)</h4>
        <textarea
          data-testid="picti-word-bulk-input"
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          rows={4}
          placeholder={"Coffee Break\nAll-Hands\nDonut Friday\nSprint Demo"}
          className="brutal-input w-full"
        />
        <div className="flex justify-end">
          <button data-testid="picti-word-bulk-add" onClick={addBulk} className="brutal-btn inline-flex items-center gap-1">
            <Plus size={14}/> Import {bulkText.split(/[\n,]/).filter((s) => s.trim()).length || 0} word(s)
          </button>
        </div>
      </div>

      {words.length === 0 ? (
        <p className="text-sm text-neutral-600">{`No custom words yet — Pictionary draws from the 36 built-in defaults. Add some company words above (departments, projects, in-jokes) and they'll appear in future rounds.`}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {words.map((w) => (
            <span key={w.id} data-testid={`picti-word-${w.id}`} className="inline-flex items-center gap-2 border-2 border-black px-3 py-1 text-sm font-bold uppercase bg-[#FFFCE5]">
              {w.word}
              <button onClick={() => remove(w.id)} className="hover:text-red-600" title="Remove"><Trash2 size={12}/></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default PictionaryWordsPanel;
